import React, { useState } from "react";
import { useLanguage, useT } from "../../lib/LanguageContext";
import { AFFILIATE_PATHS, REFERRAL_STATES } from "../../affiliate/contract";
import { formatMoneyCode } from "../../lib/formatMoney";
import LoadFailure from "../../components/ui/LoadFailure";
import usePagedList from "../../hooks/usePagedList";
import { affiliateErrorText } from "./errorText";
import { fmtDate, EMPTY } from "./format";
import Pager from "../../components/ui/Pager";

/** The four referral states, each with its own key: `expired` earned its end, `voided` did
 *  not, and the two must not render alike. A built key would be invisible to the i18n
 *  coverage scan, so the map is explicit. */
const STATE_KEYS = {
  linked: "aff.referralState.linked",
  active: "aff.referralState.active",
  expired: "aff.referralState.expired",
  voided: "aff.referralState.voided",
};
const STATE_STYLES = {
  linked: "bg-slate-50 text-slate-600 border-slate-200",
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  expired: "bg-amber-50 text-amber-800 border-amber-200",
  voided: "bg-rose-50 text-rose-700 border-rose-200",
};

/**
 * The practices this affiliate introduced.
 *
 * The referred practice travels as a NAME and nothing else — no owner email, no country, no
 * tenant id: an affiliate is owed a readable list of who they introduced, not a cross-tenant
 * handle they could address other endpoints with.
 *
 * `activatedAt`, `windowEndsAt` and the earnings are stamped TOGETHER by the first paid
 * invoice, so a `linked` row shows the placeholder in all three columns rather than an
 * invented zero date: a practice that signs up and never pays starts no clock.
 */
export default function ReferralsTable() {
  const t = useT("admin");
  const tv = useT("common");
  const { lang } = useLanguage();
  const [status, setStatus] = useState("");

  const list = usePagedList(AFFILIATE_PATHS.tenantReferrals, status ? { status } : {});

  return (
    <section className="bg-white rounded-2xl border border-eh-border-soft overflow-hidden" data-testid="affiliate-referrals">
      <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-eh-border-soft">
        <h2 className="font-display text-lg text-eh-ink">{t("aff.referrals.title")}</h2>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="referrals-status-filter"
          className="bg-white border border-eh-border-soft rounded-lg px-3 py-1.5 text-sm"
        >
          <option value="">{t("aff.referrals.filterAll")}</option>
          {REFERRAL_STATES.map((s) => (
            <option key={s} value={s}>{t(STATE_KEYS[s])}</option>
          ))}
        </select>
      </div>

      {list.loadError ? (
        <div className="p-5">
          <LoadFailure
            message={affiliateErrorText(list.loadError, { t, tv, fallback: tv("errors.loadFailed") })}
            onRetry={list.reload}
            retryLabel={tv("actions.retry")}
            testId="affiliate-referrals-load-error"
            retryTestId="affiliate-referrals-retry"
          />
        </div>
      ) : list.loaded && list.rows.length === 0 ? (
        <div className="p-8 text-center text-eh-muted text-sm" data-testid="affiliate-referrals-empty">
          {t("aff.referrals.empty")}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-eh-muted bg-eh-cream/30">
                <tr>
                  <th className="text-left px-4 py-2">{t("aff.referrals.colPractice")}</th>
                  <th className="text-left px-4 py-2">{t("aff.referrals.colStatus")}</th>
                  <th className="text-left px-4 py-2">{t("aff.referrals.colLinkedAt")}</th>
                  <th className="text-left px-4 py-2">{t("aff.referrals.colActivatedAt")}</th>
                  <th className="text-left px-4 py-2">{t("aff.referrals.colWindowEndsAt")}</th>
                  <th className="text-right px-4 py-2">{t("aff.referrals.colInvoices")}</th>
                  <th className="text-right px-4 py-2">{t("aff.referrals.colEarned")}</th>
                </tr>
              </thead>
              <tbody>
                {list.rows.map((r) => (
                  <tr key={r.id} className="border-t border-eh-border-soft" data-testid={`referral-${r.id}`}>
                    <td className="px-4 py-2 text-eh-ink">{r.referredPracticeName || EMPTY}</td>
                    <td className="px-4 py-2">
                      <span
                        data-testid={`referral-status-${r.id}`}
                        className={`inline-flex items-center text-[11px] px-2 py-0.5 rounded-full border ${
                          STATE_STYLES[r.status] || "bg-slate-50 text-slate-600 border-slate-200"}`}
                      >
                        {STATE_KEYS[r.status] ? t(STATE_KEYS[r.status]) : r.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-eh-muted">{fmtDate(r.linkedAt, lang)}</td>
                    <td className="px-4 py-2 text-eh-muted">{fmtDate(r.activatedAt, lang)}</td>
                    <td className="px-4 py-2 text-eh-muted">{fmtDate(r.windowEndsAt, lang)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{r.invoicesAccrued}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-medium">
                      {formatMoneyCode(r.earnedCents, r.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager
            page={list.page}
            pageSize={list.pageSize}
            total={list.total}
            shown={list.rows.length}
            onPage={list.setPage}
            infoText={(vars) => t("aff.page.info", vars)}
            prevLabel={t("aff.page.prev")}
            nextLabel={t("aff.page.next")}
            testId="referrals-page"
          />
        </>
      )}
    </section>
  );
}
